/**
 * Zoom-ul graficelor Connection Quality - socotelile, fara React.
 *
 * Tragi cu mouse-ul peste un grafic, pe orizontala: bucata aleasa devine
 * intervalul TUTUROR graficelor ecranului, iar serverul il aduce din nou, cu
 * galeti potriviti lungimii lui (ConnectionQualityPage::bucketFor). Aici stau
 * numai transformarile: pixel -> timp, selectie -> interval, +/- -> interval,
 * si forma din adresa (`from=2026-09-30T10:00`).
 *
 * Limitele sunt cele ale serverului: cel putin 5 minute, cel mult 30 de zile.
 */

export type CqWindow = { from: number; to: number }

export const ZOOM_MIN_MS = 5 * 60_000
export const ZOOM_MAX_MS = 30 * 86_400_000

/** Sub atatia pixeli, o apasare nu e o tragere - e un clic. */
export const DRAG_MIN_PX = 5

export type CqGeometry = { left: number; right: number; t0: number; t1: number }

/** Timpul (ms) de sub pixelul `px`, prins intre marginile graficului. */
export function pxToTime(px: number, g: CqGeometry): number {
  const width = g.right - g.left
  if (!(width > 0)) return g.t0
  const f = Math.min(1, Math.max(0, (px - g.left) / width))
  return g.t0 + f * (g.t1 - g.t0)
}

/**
 * Selectia trasa intre pixelii `a` si `b` (in orice ordine), ca interval de
 * timp. Null cand e prea scurta ca sa fie o tragere. Un interval sub 5 minute
 * se lateste in jurul mijlocului lui, fara sa iasa din grafic.
 */
export function selectionToWindow(a: number, b: number, g: CqGeometry): CqWindow | null {
  if (Math.abs(a - b) < DRAG_MIN_PX) return null
  const from = pxToTime(Math.min(a, b), g)
  const to = pxToTime(Math.max(a, b), g)
  return fit({ from, to }, g.t0, g.t1)
}

/**
 * Butoanele +/- (si singurul zoom pe ecranele tactile): `factor` < 1 strange
 * intervalul in jurul mijlocului, > 1 il lateste. Nu trece de "acum" si nici
 * de limitele serverului.
 */
export function zoomBy(w: CqWindow, factor: number, now: number): CqWindow {
  const mid = (w.from + w.to) / 2
  const span = Math.min(ZOOM_MAX_MS, Math.max(ZOOM_MIN_MS, (w.to - w.from) * factor))
  let from = mid - span / 2
  let to = mid + span / 2
  if (to > now) {
    from -= to - now
    to = now
  }
  return { from: Math.round(from), to: Math.round(to) }
}

/** Latirea la minimul de 5 minute, tinuta intre `lo` si `hi` cand se poate. */
function fit(w: CqWindow, lo: number, hi: number): CqWindow {
  let { from, to } = w
  if (to - from < ZOOM_MIN_MS) {
    const mid = (from + to) / 2
    from = mid - ZOOM_MIN_MS / 2
    to = mid + ZOOM_MIN_MS / 2
    if (hi - lo >= ZOOM_MIN_MS) {
      if (from < lo) {
        to += lo - from
        from = lo
      }
      if (to > hi) {
        from -= to - hi
        to = hi
      }
    }
  }
  if (to - from > ZOOM_MAX_MS) from = to - ZOOM_MAX_MS
  return { from: Math.round(from), to: Math.round(to) }
}

function two(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Forma din adresa si din cererea la server: `2026-09-30T10:05`, in ora
 * locala - aceeasi in care serverul scrie `from` / `to` si galetile.
 * `from` se rotunjeste in jos la minut, `to` in sus, ca sa nu se piarda nimic
 * din ce s-a ales.
 */
export function toQueryTime(ms: number, up = false): string {
  const m = up ? Math.ceil(ms / 60_000) * 60_000 : Math.floor(ms / 60_000) * 60_000
  const d = new Date(m)
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}`
}

/** Citirea inapoi a formei din adresa; null cand nu e o data. */
export function fromQueryTime(s: string | undefined | null): number | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?$/.test(s)) return null
  const ms = new Date(s.replace(' ', 'T')).getTime()
  return Number.isFinite(ms) ? ms : null
}

export type CqZoomQuery = { from: string; to: string }

export function windowToQuery(w: CqWindow): CqZoomQuery {
  return { from: toQueryTime(w.from), to: toQueryTime(w.to, true) }
}

/** Zoom-ul din adresa, daca e unul bun (ordine, 5 minute - 30 de zile). */
export function queryToZoom(from: string | undefined, to: string | undefined): CqZoomQuery | null {
  const f = fromQueryTime(from)
  const t = fromQueryTime(to)
  if (f === null || t === null || t - f < ZOOM_MIN_MS || t - f > ZOOM_MAX_MS) return null
  return { from: from!, to: to! }
}
